import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Users, Plus, Trash2, Save, Loader2 } from "lucide-react";

const emptyHeir = () => ({ name: "", relation: "", id_number: "", country: "", share_pct: 0 });

export default function LegacyPlanner() {
  const [heirs, setHeirs] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/log/legacy-plan");
        setHeirs((data.heirs || []).map((h) => ({ ...emptyHeir(), ...h })));
        setNotes(data.notes || "");
      } finally { setLoading(false); }
    })();
  }, []);

  const totalPct = heirs.reduce((a, h) => a + (parseFloat(h.share_pct) || 0), 0);
  const update = (i, k, v) => setHeirs(heirs.map((h, idx) => idx === i ? { ...h, [k]: v } : h));
  const add = () => setHeirs([...heirs, emptyHeir()]);
  const remove = (i) => setHeirs(heirs.filter((_, idx) => idx !== i));

  const save = async () => {
    if (totalPct !== 100 && heirs.length > 0) {
      toast.error(`Shares must total 100% (currently ${totalPct}%).`);
      return;
    }
    setSaving(true);
    try {
      await api.put("/log/legacy-plan", { heirs, notes });
      toast.success("Legacy plan saved.");
    } catch (e) {
      toast.error("Could not save.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="mv-card p-6 text-neutral-500">Loading…</div>;

  return (
    <div className="mv-card p-6" data-testid="legacy-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
          <Users size={18}/>
        </div>
        <div>
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Family Legacy Planner</span></div>
          <div className="font-display text-lg mv-green-text">Register your heirs — no extra legal fees.</div>
        </div>
      </div>
      <p className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
        Simple heir registration, country-specific inheritance guidance. Update anytime — your property passes with dignity.
      </p>

      <div className="mt-5 space-y-3" data-testid="heirs-list">
        {heirs.map((h, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input value={h.name} onChange={(e) => update(i, "name", e.target.value)}
              placeholder="Full name"
              className="col-span-12 md:col-span-3 px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"
              data-testid={`heir-name-${i}`}/>
            <input value={h.relation} onChange={(e) => update(i, "relation", e.target.value)}
              placeholder="Relation"
              className="col-span-6 md:col-span-2 px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
            <input value={h.id_number} onChange={(e) => update(i, "id_number", e.target.value)}
              placeholder="ID / Passport"
              className="col-span-6 md:col-span-3 px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
            <input value={h.country} onChange={(e) => update(i, "country", e.target.value)}
              placeholder="Country"
              className="col-span-6 md:col-span-2 px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
            <input type="number" min="0" max="100" value={h.share_pct}
              onChange={(e) => update(i, "share_pct", parseFloat(e.target.value) || 0)}
              placeholder="%"
              className="col-span-4 md:col-span-1 px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"
              data-testid={`heir-share-${i}`}/>
            <button onClick={() => remove(i)}
              className="col-span-2 md:col-span-1 p-2 text-red-600 hover:bg-red-50 rounded-xl"
              aria-label="Remove heir">
              <Trash2 size={16}/>
            </button>
          </div>
        ))}
      </div>

      <button onClick={add} className="btn-ghost !py-2 mt-4" data-testid="heir-add">
        <Plus size={14}/> Add heir
      </button>

      <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
        rows={2} placeholder="Notes (optional)"
        className="mt-4 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={saving} className="btn-green" data-testid="legacy-save">
          {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
          {saving ? "Saving…" : "Save Plan"}
        </button>
        <div className="text-[13px]" style={{ color: totalPct === 100 || heirs.length === 0 ? "var(--mv-muted)" : "#B33A3A" }}>
          Total shares: <b>{totalPct}%</b>{heirs.length > 0 && totalPct !== 100 && " (must total 100%)"}
        </div>
      </div>
    </div>
  );
}
